import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FormikProvider, Form, useFormik, Field } from 'formik';
import {
  Modal,
  Button,
  ButtonGroup,
  Input,
  Stack,
  Select,
  Shelf,
  Text,
  DatePicker,
} from '@chaser/components';
import usePush from '../../hooks/usePush';
import LabeledFieldWithErrorLabel from '../LabeledFieldWithErrorLabel/index.jsx';
import ChaserMultiInvoiceSelect from '../ChaserMultiInvoiceSelect';
import TaskService from '../../services/task-service';
import useShortCode from '../../hooks/useShortCode';
import { TaskStatus, TaskTypes } from '../../util/constants';
import InvoiceService from '../../services/invoice-service';
import LoadingMessage from '../LoadingMessage';
import { useCacheActions } from '../../hooks/useFetch';
import useUser from '../../hooks/useUser';
import CustomerService from '../../services/customer-service';
import ChasefeedService from '../../services/chasefeed-service';
import LabeledBlock from '../LabeledBlock';
import TwilioService from '../../services/twilio-service';
import AudioPlayer from '../AudioPlayer';

import style from './RegisterCallModal.module.scss';

const AVAILABLE_RESULTS = Object.freeze({
  ANSWERED: 'Answered',
  NOT_ANSWERED: 'Not answered',
  LEFT_VOICEMAIL: 'Left voicemail',
  INCORRECT_NUMBER: 'Incorrect number',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
});

const RecordingField = ({ recordingId, shortCode }) => {
  const { execute: getRecording, response } = usePush(TwilioService.getRecording, {
    suspense: false,
  });

  const intervalRef = useRef();
  const callCountRef = useRef(0);

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      if (callCountRef.current < 5 && !response) {
        await getRecording({
          shortCode,
          recordingId,
        });
        callCountRef.current += 1;
      } else {
        clearInterval(intervalRef.current);
      }
    }, 2000);

    return () => {
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LabeledBlock label="Recording">
      {response ? (
        <AudioPlayer src={response} />
      ) : (
        <Shelf>
          <LoadingMessage />
          <Text>Recording is processing...</Text>
        </Shelf>
      )}
      {callCountRef.current === 5 && !response && <Text>Recording unavailable.</Text>}
    </LabeledBlock>
  );
};

const RegisterCallModal = ({
  open,
  close,
  onClosed,
  task,
  callId,
  recordingId,
  contactId,
  invoice,
  result,
}) => {
  const { userId } = useUser();
  const { invalidateEndpoint } = useCacheActions();
  const shortCode = useShortCode();
  const { execute, loading: loadingTask } = usePush(
    task ? TaskService.updateTask : TaskService.createTask,
    {
      successToast: `Call reminder ${task ? 'updated' : 'created'}`,
    },
  );

  const { execute: getInvoices, response: invoiceResponse, loading: loadingInvoices } = usePush(
    InvoiceService.getAllInvoices,
  );

  const form = useFormik({
    initialValues: {
      callId,
      result: result || 'Answered',
      invoiceSelect: invoice ? [invoice] : [],
      notes: '',
      rescheduleDate: '',
    },
    validate: ({ result }) => {
      const errors = {};
      if (!result) errors.result = 'Please select a call result';
      return errors;
    },
    isInitialValid: true,
    enableReinitialize: false,
    onSubmit: async (
      { notes, result, invoiceSelect, callId, rescheduleDate },
      { setSubmitting },
    ) => {
      const invoiceVals =
        invoiceSelect.length > 0 &&
        invoiceSelect.reduce(
          (acc, curr) => {
            acc.invoiceIds.push(curr._id);
            acc.total += curr.Total;
            acc.amountDue += curr.AmountDue;
            return acc;
          },
          {
            invoiceIds: [],
            total: 0,
            amountDue: 0,
          },
        );

      const [err] = await execute({
        type: TaskTypes.CALL_REMINDER,
        taskId: task?._id,
        assignee: userId,
        metadata: {
          contact: contactId,
          invoices: invoiceVals.invoiceIds || [],
          result,
          notes,
          callId,
        },
        status: rescheduleDate && !task ? TaskStatus.OPEN : TaskStatus.COMPLETED,
        rescheduledDate: rescheduleDate === '' ? undefined : rescheduleDate,
        shortCode,
      });

      await invalidateEndpoint(InvoiceService.getInvoiceHistory);
      await invalidateEndpoint(TaskService.getTasks);
      await invalidateEndpoint(CustomerService.getCustomerHistory);
      await invalidateEndpoint(ChasefeedService.getRecentEvents);

      setSubmitting(false);
      if (err) return;
      close(true);
    },
  });

  useEffect(() => {
    if (open) {
      getInvoices({
        contactId,
        shortCode,
      });
    }
  }, [contactId, getInvoices, open, shortCode]);

  const invoices = Array.isArray(invoiceResponse) ? invoiceResponse : [];
  const loading = loadingInvoices || loadingTask;

  const callOptions = useMemo(() => {
    return Object.keys(AVAILABLE_RESULTS).map(r => AVAILABLE_RESULTS[r]);
  }, []);

  return (
    <Modal
      title="Register call"
      open={open}
      toggle={close}
      onClosed={onClosed}
      closeOnOutsideClick={false}
    >
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            {loadingInvoices ? (
              <LoadingMessage />
            ) : (
              <Stack>
                <LabeledFieldWithErrorLabel label="Result" name="result" as={Select} width="full">
                  {callOptions.map(r => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </LabeledFieldWithErrorLabel>
                <LabeledFieldWithErrorLabel
                  label="Notes"
                  name="notes"
                  type="textarea"
                  as={Input}
                  width="100%"
                  placeholder="Call notes"
                  data-testid="call-notes"
                />
                <Shelf>
                  <LabeledBlock className={style.block} label="Attached invoice(s)">
                    <Field
                      name="invoiceSelect"
                      as={ChaserMultiInvoiceSelect}
                      invoices={invoices}
                      width="50%"
                      onChange={s => form.setFieldValue('invoiceSelect', s)}
                      selected={invoice && [invoice]}
                    />
                  </LabeledBlock>
                  <LabeledBlock className={style.block} label="Reschedule">
                    <Field
                      name="rescheduleDate"
                      as={DatePicker}
                      showTimeSelect
                      shouldCloseOnSelect={false}
                      minDate={new Date()}
                      timeFormat="h:mm aa"
                      placeholder="Select date"
                      value={form.values.rescheduleDate}
                      dateFormat="dd/MM/yyyy HH:mm"
                      onChange={date => form.setFieldValue('rescheduleDate', date)}
                      disabled={loadingTask}
                    />
                  </LabeledBlock>
                </Shelf>
                {recordingId && <RecordingField recordingId={recordingId} shortCode={shortCode} />}
              </Stack>
            )}
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button appearance="secondary" onClick={() => close(true)}>
                Cancel
              </Button>
              <Button disabled={!form.isValid || loading} type="submit">
                Register
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default RegisterCallModal;
export { AVAILABLE_RESULTS };
